import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connector } from '../../../services/api';
import type { WorkflowState } from '../../../types/workflow';
import {
  buildResolvedUserState,
  errorText,
  missingPositionFields,
  parseSeat,
  parseTurn,
  REPLY_TEXT_KEYS,
  toDialog,
  turnText,
  type ChatMessage,
  type ChatSide,
  type Seat,
  type TurnResult,
} from '../../../utils/workflowTestChat';
import { Banner, Pill } from './WorkflowFormControls';
import { DefBlocks, isPlainObject } from './WorkflowDefinitionView';

export interface WorkflowTestChatsProps {
  integrationId: string;
  /** The state both branches run the turn on, before and after the proposal. */
  stateId: string | null;
  initialState: WorkflowState | undefined;
  optimizedState: WorkflowState | undefined;
  /** From the job's `base_revision` — part of the position the turn runs on. */
  workflowVersionId?: string;
}

const SIDES: ChatSide[] = ['before', 'after'];

const SIDE_COPY: Record<ChatSide, { title: string; badge: string; hint: string; placeholder: string }> = {
  before: {
    title: 'Before',
    badge: 'Live',
    hint: 'The workflow as the product runs it now',
    placeholder: 'Ask the current workflow…',
  },
  after: {
    title: 'After',
    badge: 'Proposed',
    hint: 'The same state with this run applied — nothing is written',
    placeholder: 'Ask the proposed workflow…',
  },
};

/**
 * What the turn reported, laid out by meaning rather than by JSON nesting — the same
 * reader the definition pages use, so arrays of objects become tables and short scalars
 * gather into a fact strip. The reply text is skipped: it is already the bubble above.
 *
 * The JSON tab stays because this response shape is not verified against the product
 * yet; when a field is spelled differently than expected, the raw body is the only way
 * to see it.
 */
const TurnDetails: React.FC<{ turn: TurnResult }> = ({ turn }) => {
  const [view, setView] = useState<'details' | 'json'>('details');
  const body = isPlainObject(turn.raw)
    ? isPlainObject((turn.raw as Record<string, unknown>).data)
      ? ((turn.raw as Record<string, unknown>).data as Record<string, unknown>)
      : (turn.raw as Record<string, unknown>)
    : null;

  return (
    <div className="cwx-detail">
      <div className="cwx-detail__tabs" role="group" aria-label="Turn detail format">
        {(['details', 'json'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`cwx-detail__tab ${view === tab ? 'cwx-detail__tab--active' : ''}`}
            aria-pressed={view === tab}
            onClick={() => setView(tab)}
          >
            {tab === 'details' ? 'Details' : 'JSON'}
          </button>
        ))}
      </div>
      {view === 'json' || !body ? (
        <pre className="cwx-detail__json">{JSON.stringify(turn.raw, null, 2)}</pre>
      ) : (
        <div className="cwx-detail__body">
          <DefBlocks obj={body} skip={REPLY_TEXT_KEYS} />
        </div>
      )}
    </div>
  );
};

/**
 * One message, two branches (AIT-146), plus a composer per branch for pushing just one
 * of them further. Each branch holds its own seat: the Map, long-term memory, diary and
 * portrait are written for real, so two candidates sharing an identity would differ by
 * launch order rather than by workflow (AIT-126).
 *
 * Single-turn by design: a turn does not advance the position in the graph, and
 * `routing.targets` carries no target state body, so there is nothing to step into yet.
 */
const WorkflowTestChats: React.FC<WorkflowTestChatsProps> = ({
  integrationId,
  stateId,
  initialState,
  optimizedState,
  workflowVersionId,
}) => {
  const [messages, setMessages] = useState<Record<ChatSide, ChatMessage[]>>({ before: [], after: [] });
  const [inputs, setInputs] = useState<Record<ChatSide, string>>({ before: '', after: '' });
  const [bothInput, setBothInput] = useState('');
  const [busy, setBusy] = useState<Record<ChatSide, boolean>>({ before: false, after: false });
  const [errors, setErrors] = useState<Record<ChatSide, string | null>>({ before: null, after: null });
  const [rawOpen, setRawOpen] = useState<string | null>(null);
  const seats = useRef<Partial<Record<ChatSide, Seat>>>({});

  const anyBusy = busy.before || busy.after;

  const releaseSeats = useCallback(() => {
    SIDES.forEach((side) => {
      const seat = seats.current[side];
      if (!seat) return;
      // Best effort: a leaked seat costs a stale virtual user, a thrown error costs the reset.
      void connector.releaseEphemeralSeat(integrationId, seat.seatId).catch(() => undefined);
    });
    seats.current = {};
  }, [integrationId]);

  useEffect(() => releaseSeats, [releaseSeats]);

  // A different state is a different comparison — old seats and history do not carry over.
  useEffect(() => {
    releaseSeats();
    setMessages({ before: [], after: [] });
    setErrors({ before: null, after: null });
  }, [stateId, releaseSeats]);

  const seatFor = async (side: ChatSide): Promise<Seat> => {
    const existing = seats.current[side];
    if (existing) return existing;
    const payload = await connector.takeEphemeralSeat(integrationId);
    const seat = parseSeat(payload);
    if (!seat) {
      throw new Error(`The seat response carried no recognisable id. Raw: ${JSON.stringify(payload).slice(0, 400)}`);
    }
    seats.current[side] = seat;
    return seat;
  };

  const runSide = async (side: ChatSide, dialog: ChatMessage[]) => {
    const definition = side === 'before' ? initialState : optimizedState;
    if (!definition) throw new Error(`No ${side} definition for this state — the graph and the proposal disagree.`);
    const seat = await seatFor(side);
    const resolved = buildResolvedUserState({ seat, stateId: stateId!, definition, workflowVersionId });

    // The server reports a missing position as eight separate 422 leaves; saying which
    // field the seat did not supply is more use than relaying that list.
    const missing = missingPositionFields(resolved);
    if (missing.length > 0) {
      throw new Error(
        `The seat did not supply ${missing.join(', ')}, and the turn cannot be placed without it. ` +
          `Raw seat: ${JSON.stringify(seat.raw).slice(0, 300)}`
      );
    }

    const payload = await connector.replyDialogWithConfig(integrationId, {
      // The roster inside the definition is what the turn runs on; `config` stays for the
      // two prompts the product still reads from it.
      config: JSON.stringify({}),
      user_id: seat.userId,
      state: resolved,
      routing: seat.routing,
      dialog: toDialog(dialog),
      rag_enabled: true,
      ltm_enabled: true,
      diary_enabled: true,
      portrait_enabled: true,
      chat_id: `wf-${side}-${seat.seatId}`,
    });
    return parseTurn(payload);
  };

  /**
   * Sends one message down the named branches. Both at once keeps the histories aligned
   * for comparison; one at a time lets a branch be pushed further on its own, after which
   * the two dialogues are deliberately no longer the same.
   */
  const sendTo = async (sides: ChatSide[], text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !stateId) return;
    if (sides.some((side) => busy[side])) return;

    const userMessage: ChatMessage = { id: `u-${Date.now()}`, role: 'user', content: trimmed };
    const dialogs: Partial<Record<ChatSide, ChatMessage[]>> = {};
    sides.forEach((side) => {
      dialogs[side] = [...messages[side], userMessage];
    });

    setMessages((prev) => {
      const next = { ...prev };
      sides.forEach((side) => {
        next[side] = dialogs[side]!;
      });
      return next;
    });
    setBusy((prev) => {
      const next = { ...prev };
      sides.forEach((side) => {
        next[side] = true;
      });
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      sides.forEach((side) => {
        next[side] = null;
      });
      return next;
    });

    const results = await Promise.allSettled(sides.map((side) => runSide(side, dialogs[side]!)));

    setMessages((prev) => {
      const next = { ...prev };
      sides.forEach((side, index) => {
        const result = results[index];
        if (result.status !== 'fulfilled') return;
        next[side] = [
          ...dialogs[side]!,
          { id: `a-${side}-${Date.now()}`, role: 'assistant', content: turnText(result.value), turn: result.value },
        ];
      });
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      sides.forEach((side, index) => {
        const result = results[index];
        next[side] = result.status === 'rejected' ? errorText(result.reason) : null;
      });
      return next;
    });
    setBusy((prev) => {
      const next = { ...prev };
      sides.forEach((side) => {
        next[side] = false;
      });
      return next;
    });
  };

  const sendBoth = async () => {
    const text = bothInput;
    setBothInput('');
    await sendTo(SIDES, text);
  };

  const sendOne = async (side: ChatSide) => {
    const text = inputs[side];
    setInputs((prev) => ({ ...prev, [side]: '' }));
    await sendTo([side], text);
  };

  const reset = () => {
    releaseSeats();
    setMessages({ before: [], after: [] });
    setErrors({ before: null, after: null });
    setInputs({ before: '', after: '' });
    setBothInput('');
    setRawOpen(null);
  };

  if (!stateId) return null;

  const diverged = messages.before.length !== messages.after.length;

  return (
    <section className="cwx" data-testid="cwt-test-chats">
      <header className="cwx__head">
        <div className="cwx__intro">
          <span className="cwx__eyebrow">Try it</span>
          <h3>Test chat · before and after</h3>
          <p>
            One message to both branches, or a message to just one, all running on <code>{stateId}</code>. The
            right-hand branch uses the proposed workflow without applying it — the product keeps running as it is.
          </p>
        </div>
        <button type="button" className="cwf-btn cwf-btn--xs" onClick={reset} disabled={anyBusy}>
          <i className="fas fa-rotate-left" aria-hidden="true" /> Reset
        </button>
      </header>

      <Banner tone="warn" title="Dev only, and one turn at a time">
        <p>
          This runs through the connector's internal contour with a service key, so it exists only in a dev build. A
          turn does not move the position in the graph, so the dialogue cannot continue past a state transition yet —
          each message is answered from <code>{stateId}</code> again. Replies are real product turns: the Map,
          long-term memory, diary and portrait are written for the seat.
        </p>
      </Banner>

      <div className="cwx__grid">
        {SIDES.map((side) => (
          <div key={side} className={`cwx-chat cwx-chat--${side}`}>
            <div className="cwx-chat__head">
              <span className="cwx-chat__badge">{SIDE_COPY[side].badge}</span>
              <h4>{SIDE_COPY[side].title}</h4>
              <p>{SIDE_COPY[side].hint}</p>
            </div>
            <div className="cwx-chat__messages" data-testid={`cwx-messages-${side}`}>
              {messages[side].length === 0 ? (
                <div className="cwx-chat__empty">Send a message to run this branch.</div>
              ) : (
                messages[side].map((message) => (
                  <div key={message.id} className={`cwx-msg cwx-msg--${message.role}`}>
                    <div className="cwx-msg__body">{message.content}</div>
                    {message.turn ? (
                      <div className="cwx-msg__facts">
                        {message.turn.intent?.wouldMove ? (
                          <Pill tone="accent" title="Where this branch would go next">
                            → {message.turn.intent.wouldMove}
                          </Pill>
                        ) : null}
                        {message.turn.events > 0 ? <span>{message.turn.events} events</span> : null}
                        {message.turn.patchOps > 0 ? <span>{message.turn.patchOps} state ops</span> : null}
                        {message.turn.usage?.tokens != null ? (
                          <span>{message.turn.usage.tokens.toLocaleString()} tokens</span>
                        ) : null}
                        {message.turn.usage?.cost != null ? <span>${message.turn.usage.cost.toFixed(4)}</span> : null}
                        <button
                          type="button"
                          className="cwx-msg__raw"
                          onClick={() => setRawOpen(rawOpen === message.id ? null : message.id)}
                          aria-expanded={rawOpen === message.id}
                        >
                          {rawOpen === message.id ? 'Hide details' : 'Details'}
                        </button>
                      </div>
                    ) : null}

                  </div>
                ))
              )}
              {busy[side] ? (
                <div className="cwx-chat__pending">
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Running a full product turn…
                </div>
              ) : null}
            </div>

            {/* Below the message list, not inside it: the list scrolls and is capped, which
                left the detail clipped and fighting two scrollbars. */}
            {(() => {
              const opened = messages[side].find((message) => message.id === rawOpen && message.turn);
              return opened?.turn ? <TurnDetails turn={opened.turn} /> : null;
            })()}

            {errors[side] ? (
              <Banner tone="error" title={`The ${side} branch failed`}>
                <p>{errors[side]}</p>
              </Banner>
            ) : null}

            {/* Per-branch composer: pushes this side on alone, leaving the other where it is. */}
            <div className="cwx-chat__composer">
              <input
                className="cwf-input cwf-input--sm"
                placeholder={SIDE_COPY[side].placeholder}
                aria-label={`Message the ${side} branch only`}
                value={inputs[side]}
                onChange={(e) => setInputs((prev) => ({ ...prev, [side]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void sendOne(side);
                  }
                }}
                disabled={busy[side]}
              />
              <button
                type="button"
                className="cwf-btn cwf-btn--xs"
                onClick={() => void sendOne(side)}
                disabled={busy[side] || !inputs[side].trim()}
                title={`Send to ${side} only`}
              >
                <i className={`fas ${busy[side] ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} aria-hidden="true" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {diverged ? (
        <p className="cwx__note">
          The two branches no longer hold the same dialogue — one was sent a message the other was not. Reset to line
          them back up.
        </p>
      ) : null}

      <div className="cwx__composer">
        <input
          className="cwf-input"
          placeholder="Say something a client would say…"
          aria-label="Message both branches"
          value={bothInput}
          onChange={(e) => setBothInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void sendBoth();
            }
          }}
          disabled={anyBusy}
        />
        <button
          type="button"
          className="cwf-btn cwf-btn--sm cwf-btn--primary"
          onClick={() => void sendBoth()}
          disabled={anyBusy || !bothInput.trim()}
        >
          <i className={`fas ${anyBusy ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} aria-hidden="true" /> Send to both
        </button>
      </div>
    </section>
  );
};

export default WorkflowTestChats;
